import { PDFDocument, rgb, StandardFonts, PDFPage, PDFFont } from 'pdf-lib';
import { DocumentModel, PaperSize, DocumentSection, DocumentBlock, TextBlock, FormulaBlock, TableBlock, ImageBlock, ShapeBlock } from '../ast/document.types';
import { sanitizeFilename } from './documentValidator';

// Dimensions in points (72 points = 1 inch, 1 mm = 2.83465 pt)
const PAGE_DIMENSIONS: Record<PaperSize, [number, number]> = {
  A4: [595.28, 841.89],
  Letter: [612.0, 792.0],
  Legal: [612.0, 1008.0],
};

const GREEK_MATH_MAP: Record<string, string> = {
  'α': 'alpha', 'β': 'beta', 'γ': 'gamma', 'δ': 'delta', 'Δ': 'Delta',
  'ε': 'epsilon', 'ζ': 'zeta', 'η': 'eta', 'θ': 'theta', 'Θ': 'Theta',
  'ι': 'iota', 'κ': 'kappa', 'λ': 'lambda', 'Λ': 'Lambda', 'μ': 'u',
  'ν': 'nu', 'ξ': 'xi', 'π': 'pi', 'Π': 'Pi', 'ρ': 'rho',
  'σ': 'sigma', 'Σ': 'Sigma', 'τ': 'tau', 'υ': 'upsilon', 'φ': 'phi',
  'Φ': 'Phi', 'χ': 'chi', 'ψ': 'psi', 'Ψ': 'Psi', 'ω': 'omega', 'Ω': 'Ohm',
  '≈': '~=', '≠': '!=', '≤': '<=', '≥': '>=', '±': '+/-', '×': 'x',
  '÷': '/', '•': '*', '→': '->', '←': '<-', '⇒': '=>', '∞': 'inf',
  '°': ' deg', '√': 'sqrt', '∫': 'integral', '∂': 'd', '∇': 'grad',
  '—': '--', '–': '-', '“': '"', '”': '"', '‘': "'", '’': "'", '…': '...',
};

/**
 * Sanitizes characters to ensure full compatibility with WinAnsi standard fonts
 */
export function cleanTextForPdf(input: string): string {
  if (!input) return '';
  let result = input;
  for (const [char, replacement] of Object.entries(GREEK_MATH_MAP)) {
    result = result.split(char).join(replacement);
  }
  // Replace any remaining non-ASCII character with ASCII safe approximation
  return result.replace(/[^\x00-\xFF]/g, '?');
}

export interface ExportPdfOptions {
  isGrayscale?: boolean;
}

interface RenderContext {
  pdfDoc: PDFDocument;
  fontRegular: PDFFont;
  fontBold: PDFFont;
  fontItalic: PDFFont;
  pageWidth: number;
  pageHeight: number;
  marginLeft: number;
  marginRight: number;
  marginTop: number;
  marginBottom: number;
  contentWidth: number;
  currentPage: PDFPage;
  currentY: number;
  pageNumber: number;
  pages: PDFPage[];
  docModel: DocumentModel;
  isGrayscale?: boolean;
}

/**
 * Exports a DocumentModel AST directly to a high-fidelity vector PDF
 */
export async function exportDocumentToPdf(doc: DocumentModel, options?: ExportPdfOptions): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();

  // Load standard fonts
  const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const fontItalic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);

  // Setup page metrics
  const paper = doc.pageSettings?.paperSize || 'A4';
  const orientation = doc.pageSettings?.orientation || 'portrait';
  let [w, h] = PAGE_DIMENSIONS[paper] || PAGE_DIMENSIONS.A4;
  if (orientation === 'landscape') {
    [w, h] = [h, w];
  }

  const mmToPt = 2.83465;
  const marginLeft = (doc.pageSettings?.margins?.leftMm || 25.4) * mmToPt;
  const marginRight = (doc.pageSettings?.margins?.rightMm || 25.4) * mmToPt;
  const marginTop = (doc.pageSettings?.margins?.topMm || 25.4) * mmToPt;
  const marginBottom = (doc.pageSettings?.margins?.bottomMm || 25.4) * mmToPt;
  const contentWidth = w - marginLeft - marginRight;

  const firstPage = pdfDoc.addPage([w, h]);
  const ctx: RenderContext = {
    pdfDoc,
    fontRegular,
    fontBold,
    fontItalic,
    pageWidth: w,
    pageHeight: h,
    marginLeft,
    marginRight,
    marginTop,
    marginBottom,
    contentWidth,
    currentPage: firstPage,
    currentY: h - marginTop,
    pageNumber: 1,
    pages: [firstPage],
    docModel: doc,
    isGrayscale: !!options?.isGrayscale,
  };

  // If this is a cover-page document or has cover type, render cover page
  const isCoverOnly = doc.metadata.documentType === 'cover_page';
  const coverSection = doc.sections.find((s) => s.type === 'cover' && s.enabled);

  if (coverSection && coverSection.blocks.length > 0) {
    for (const block of coverSection.blocks) {
      await renderBlock(ctx, block);
    }
    if (!isCoverOnly && doc.sections.some((s) => s.type !== 'cover' && s.enabled)) {
      addNewPage(ctx);
    }
  } else if (isCoverOnly || coverSection) {
    await renderCoverPage(ctx);
    if (!isCoverOnly && doc.sections.some((s) => s.type !== 'cover' && s.enabled)) {
      addNewPage(ctx);
    }
  }

  // Render normal structured sections
  for (const section of doc.sections) {
    if (!section.enabled || section.type === 'cover') continue;

    if (section.settings?.pageBreakBefore && ctx.currentY < h - marginTop - 20) {
      addNewPage(ctx);
    }

    await renderSection(ctx, section);
  }

  // Apply Headers and Footers to all non-cover pages
  applyHeadersAndFooters(ctx);

  return await pdfDoc.save();
}

/**
 * Adds a new page to the document context
 */
function addNewPage(ctx: RenderContext): PDFPage {
  const newPage = ctx.pdfDoc.addPage([ctx.pageWidth, ctx.pageHeight]);
  ctx.currentPage = newPage;
  ctx.currentY = ctx.pageHeight - ctx.marginTop;
  ctx.pageNumber += 1;
  ctx.pages.push(newPage);
  return newPage;
}

/**
 * Checks remaining vertical space, and wraps to a new page if required
 */
function ensureVerticalSpace(ctx: RenderContext, requiredPt: number) {
  if (ctx.currentY - requiredPt < ctx.marginBottom) {
    addNewPage(ctx);
  }
}

/**
 * Renders an academic cover page
 */
async function renderCoverPage(ctx: RenderContext) {
  const meta = ctx.docModel.metadata;
  const page = ctx.currentPage;
  const centerX = ctx.pageWidth / 2;

  // Header banner / Institution Name
  let y = ctx.pageHeight - ctx.marginTop - 30;

  // Institution Short Name / Top Pill
  const shortName = cleanTextForPdf(meta.institution?.shortName || 'EWU');
  page.drawText(shortName, {
    x: centerX - (ctx.fontBold.widthOfTextAtSize(shortName, 11) / 2),
    y,
    size: 11,
    font: ctx.fontBold,
    color: rgb(0.12, 0.4, 0.8),
  });

  y -= 22;
  const instName = cleanTextForPdf(meta.institution?.name || 'East West University');
  page.drawText(instName, {
    x: centerX - (ctx.fontBold.widthOfTextAtSize(instName, 18) / 2),
    y,
    size: 18,
    font: ctx.fontBold,
    color: rgb(0.08, 0.1, 0.15),
  });

  if (meta.institution?.department) {
    y -= 16;
    const dept = cleanTextForPdf(meta.institution.department);
    page.drawText(dept, {
      x: centerX - (ctx.fontRegular.widthOfTextAtSize(dept, 11) / 2),
      y,
      size: 11,
      font: ctx.fontRegular,
      color: rgb(0.35, 0.38, 0.45),
    });
  }

  // Decorative Accent Line
  y -= 20;
  page.drawLine({
    start: { x: ctx.marginLeft + 40, y },
    end: { x: ctx.pageWidth - ctx.marginRight - 40, y },
    thickness: 1.5,
    color: rgb(0.12, 0.4, 0.8),
  });

  // Course Code & Title
  y -= 45;
  if (meta.courseCode) {
    const codeText = cleanTextForPdf(`COURSE: ${meta.courseCode.toUpperCase()}`);
    page.drawText(codeText, {
      x: centerX - (ctx.fontBold.widthOfTextAtSize(codeText, 11) / 2),
      y,
      size: 11,
      font: ctx.fontBold,
      color: rgb(0.2, 0.45, 0.85),
    });
    y -= 16;
  }

  if (meta.courseTitle) {
    const titleText = cleanTextForPdf(meta.courseTitle);
    page.drawText(titleText, {
      x: centerX - (ctx.fontRegular.widthOfTextAtSize(titleText, 12) / 2),
      y,
      size: 12,
      font: ctx.fontRegular,
      color: rgb(0.2, 0.22, 0.28),
    });
    y -= 25;
  }

  // Document Title Box
  y -= 25;
  const title = cleanTextForPdf(meta.title || 'Laboratory Report');
  const titleLines = wrapText(title, ctx.fontBold, 20, ctx.contentWidth - 60);
  
  for (const line of titleLines) {
    page.drawText(line, {
      x: centerX - (ctx.fontBold.widthOfTextAtSize(line, 20) / 2),
      y,
      size: 20,
      font: ctx.fontBold,
      color: rgb(0.05, 0.08, 0.12),
    });
    y -= 26;
  }

  if (meta.subtitle) {
    y -= 4;
    const subText = cleanTextForPdf(meta.subtitle);
    page.drawText(subText, {
      x: centerX - (ctx.fontItalic.widthOfTextAtSize(subText, 12) / 2),
      y,
      size: 12,
      font: ctx.fontItalic,
      color: rgb(0.4, 0.43, 0.5),
    });
    y -= 20;
  }

  // Student & Instructor Dual Columns
  y = Math.min(y - 40, ctx.pageHeight * 0.42);

  const colWidth = (ctx.contentWidth - 30) / 2;
  const col1X = ctx.marginLeft;
  const col2X = ctx.marginLeft + colWidth + 30;

  // Submitted By Box
  page.drawRectangle({
    x: col1X,
    y: y - 110,
    width: colWidth,
    height: 120,
    borderColor: rgb(0.85, 0.88, 0.92),
    borderWidth: 1,
    color: rgb(0.97, 0.98, 1.0),
  });

  page.drawText('SUBMITTED BY', {
    x: col1X + 14,
    y: y - 10,
    size: 9,
    font: ctx.fontBold,
    color: rgb(0.12, 0.4, 0.8),
  });

  page.drawText(cleanTextForPdf(meta.author.studentName || 'Student Name'), {
    x: col1X + 14,
    y: y - 30,
    size: 12,
    font: ctx.fontBold,
    color: rgb(0.1, 0.12, 0.15),
  });

  if (meta.author.studentId) {
    page.drawText(cleanTextForPdf(`ID: ${meta.author.studentId}`), {
      x: col1X + 14,
      y: y - 48,
      size: 10,
      font: ctx.fontRegular,
      color: rgb(0.3, 0.33, 0.4),
    });
  }

  if (meta.author.sectionOrGroup) {
    page.drawText(cleanTextForPdf(`Section: ${meta.author.sectionOrGroup}`), {
      x: col1X + 14,
      y: y - 64,
      size: 10,
      font: ctx.fontRegular,
      color: rgb(0.3, 0.33, 0.4),
    });
  }

  // Submitted To Box
  page.drawRectangle({
    x: col2X,
    y: y - 110,
    width: colWidth,
    height: 120,
    borderColor: rgb(0.85, 0.88, 0.92),
    borderWidth: 1,
    color: rgb(0.98, 0.98, 0.99),
  });

  page.drawText('SUBMITTED TO', {
    x: col2X + 14,
    y: y - 10,
    size: 9,
    font: ctx.fontBold,
    color: rgb(0.3, 0.4, 0.5),
  });

  page.drawText(cleanTextForPdf(meta.instructor.instructorName || 'Instructor Name'), {
    x: col2X + 14,
    y: y - 30,
    size: 12,
    font: ctx.fontBold,
    color: rgb(0.1, 0.12, 0.15),
  });

  if (meta.instructor.designation) {
    page.drawText(cleanTextForPdf(meta.instructor.designation), {
      x: col2X + 14,
      y: y - 48,
      size: 10,
      font: ctx.fontRegular,
      color: rgb(0.3, 0.33, 0.4),
    });
  }

  if (meta.instructor.department) {
    page.drawText(cleanTextForPdf(meta.instructor.department), {
      x: col2X + 14,
      y: y - 64,
      size: 10,
      font: ctx.fontRegular,
      color: rgb(0.3, 0.33, 0.4),
    });
  }

  // Submission Date at Bottom
  const dateStr = cleanTextForPdf(meta.submissionDate ? `Date of Submission: ${meta.submissionDate}` : `Generated: ${new Date().toLocaleDateString()}`);
  page.drawText(dateStr, {
    x: centerX - (ctx.fontRegular.widthOfTextAtSize(dateStr, 10) / 2),
    y: ctx.marginBottom + 30,
    size: 10,
    font: ctx.fontRegular,
    color: rgb(0.4, 0.43, 0.5),
  });
}

/**
 * Renders a structured section and its blocks
 */
async function renderSection(ctx: RenderContext, section: DocumentSection) {
  ensureVerticalSpace(ctx, 40);

  // Section Heading Bar
  const headingText = cleanTextForPdf(section.title.toUpperCase());
  ctx.currentPage.drawText(headingText, {
    x: ctx.marginLeft,
    y: ctx.currentY,
    size: 13,
    font: ctx.fontBold,
    color: rgb(0.12, 0.2, 0.35),
  });

  // Underline
  ctx.currentY -= 6;
  ctx.currentPage.drawLine({
    start: { x: ctx.marginLeft, y: ctx.currentY },
    end: { x: ctx.pageWidth - ctx.marginRight, y: ctx.currentY },
    thickness: 1,
    color: rgb(0.82, 0.85, 0.9),
  });

  ctx.currentY -= 16;

  // Render Blocks
  for (const block of section.blocks) {
    await renderBlock(ctx, block);
  }

  ctx.currentY -= 12; // Extra gap between sections
}

function parseHexColor(hex?: string, isGrayscale?: boolean) {
  if (!hex || hex === 'transparent') return null;
  const clean = hex.replace('#', '');
  if (clean.length === 6) {
    let r = parseInt(clean.substring(0, 2), 16) / 255;
    let g = parseInt(clean.substring(2, 4), 16) / 255;
    let b = parseInt(clean.substring(4, 6), 16) / 255;
    if (isGrayscale) {
      const gray = 0.299 * r + 0.587 * g + 0.114 * b;
      return rgb(gray, gray, gray);
    }
    return rgb(r, g, b);
  }
  return isGrayscale ? rgb(0.3, 0.3, 0.3) : rgb(0.12, 0.23, 0.54);
}

function renderShapeBlock(ctx: RenderContext, block: ShapeBlock) {
  const { shapeType = 'rectangle', width = 160, height = 80, fill, stroke, strokeWidth = 1.5, alignment = 'center', offsetX = 0, offsetY = 0, opacity = 1 } = block.data;
  const targetH = Math.min(height, 300);
  const targetW = Math.min(width, ctx.contentWidth);
  ensureVerticalSpace(ctx, targetH + 20);

  let shapeX = ctx.marginLeft + offsetX;
  if (alignment === 'center') {
    shapeX = ctx.marginLeft + (ctx.contentWidth - targetW) / 2 + offsetX;
  } else if (alignment === 'right') {
    shapeX = ctx.pageWidth - ctx.marginRight - targetW + offsetX;
  }

  const shapeY = ctx.currentY - targetH + offsetY;
  const fillColor = parseHexColor(fill, ctx.isGrayscale);
  const strokeColor = parseHexColor(stroke, ctx.isGrayscale) || (ctx.isGrayscale ? rgb(0.2, 0.2, 0.2) : rgb(0.1, 0.2, 0.4));

  if (shapeType === 'circle' || shapeType === 'ellipse') {
    ctx.currentPage.drawEllipse({
      x: shapeX + targetW / 2,
      y: shapeY + targetH / 2,
      xScale: Math.max(2, targetW / 2),
      yScale: Math.max(2, targetH / 2),
      color: fillColor || undefined,
      borderColor: strokeColor,
      borderWidth: strokeWidth,
      opacity,
    });
  } else if (shapeType === 'line' || shapeType === 'divider') {
    ctx.currentPage.drawLine({
      start: { x: shapeX, y: shapeY + targetH / 2 },
      end: { x: shapeX + targetW, y: shapeY + targetH / 2 },
      thickness: strokeWidth,
      color: strokeColor,
      opacity,
    });
  } else {
    ctx.currentPage.drawRectangle({
      x: shapeX,
      y: shapeY,
      width: targetW,
      height: targetH,
      color: fillColor || undefined,
      borderColor: strokeColor,
      borderWidth: strokeWidth,
      opacity,
    });
  }

  ctx.currentY = shapeY - 10;
}

/**
 * Dispatches block rendering based on block type
 */
async function renderBlock(ctx: RenderContext, block: DocumentBlock) {
  switch (block.type) {
    case 'text':
      renderTextBlock(ctx, block);
      break;
    case 'formula':
      renderFormulaBlock(ctx, block);
      break;
    case 'table':
      renderTableBlock(ctx, block);
      break;
    case 'image':
      await renderImageBlock(ctx, block);
      break;
    case 'shape':
      renderShapeBlock(ctx, block);
      break;
    case 'page_break':
      addNewPage(ctx);
      break;
  }
}

/**
 * Renders formatted multiline text
 */
function renderTextBlock(ctx: RenderContext, block: TextBlock) {
  const text = cleanTextForPdf(block.data.text || '');
  if (!text.trim()) return;

  const level = block.data.level || 'paragraph';
  const formatting = block.data.formatting || {};

  let fontSize = formatting.fontSizePt || 10.5;
  let font = formatting.bold ? ctx.fontBold : formatting.italic ? ctx.fontItalic : ctx.fontRegular;
  let lineHeight = fontSize * 1.38;
  let textColor = ctx.isGrayscale ? rgb(0.14, 0.14, 0.14) : rgb(0.12, 0.14, 0.18);

  if (formatting.color) {
    const customColor = parseHexColor(formatting.color, ctx.isGrayscale);
    if (customColor) textColor = customColor;
  } else if (level === 'heading1') {
    fontSize = formatting.fontSizePt || 14;
    font = ctx.fontBold;
    lineHeight = fontSize * 1.3;
    textColor = ctx.isGrayscale ? rgb(0.1, 0.1, 0.1) : rgb(0.1, 0.15, 0.25);
    ctx.currentY -= 6;
  } else if (level === 'heading2') {
    fontSize = formatting.fontSizePt || 12;
    font = ctx.fontBold;
    lineHeight = fontSize * 1.3;
    textColor = ctx.isGrayscale ? rgb(0.15, 0.15, 0.15) : rgb(0.15, 0.2, 0.3);
    ctx.currentY -= 4;
  } else if (level === 'heading3') {
    fontSize = formatting.fontSizePt || 11;
    font = ctx.fontBold;
    lineHeight = fontSize * 1.35;
    ctx.currentY -= 2;
  }

  const offX = formatting.offsetX || 0;
  const offY = formatting.offsetY || 0;

  const lines = text.split('\n');
  for (const rawLine of lines) {
    if (rawLine.trim().length === 0) {
      ctx.currentY -= lineHeight * 0.8;
      continue;
    }

    const wrapped = wrapText(rawLine, font, fontSize, ctx.contentWidth);
    for (const line of wrapped) {
      ensureVerticalSpace(ctx, lineHeight);
      
      let x = ctx.marginLeft + offX;
      if (formatting.alignment === 'center') {
        x = (ctx.pageWidth - font.widthOfTextAtSize(line, fontSize)) / 2 + offX;
      } else if (formatting.alignment === 'right') {
        x = ctx.pageWidth - ctx.marginRight - font.widthOfTextAtSize(line, fontSize) + offX;
      }

      ctx.currentPage.drawText(line, {
        x,
        y: ctx.currentY + offY,
        size: fontSize,
        font,
        color: textColor,
      });

      ctx.currentY -= lineHeight;
    }
  }

  ctx.currentY -= 4;
}

/**
 * Renders formula blocks with academic formatting
 */
function renderFormulaBlock(ctx: RenderContext, block: FormulaBlock) {
  const latex = block.data.latex || '';
  if (!latex.trim()) return;

  const boxHeight = 44;
  ensureVerticalSpace(ctx, boxHeight + 20);

  const boxY = ctx.currentY - boxHeight;

  // Background Box
  ctx.currentPage.drawRectangle({
    x: ctx.marginLeft,
    y: boxY,
    width: ctx.contentWidth,
    height: boxHeight,
    color: rgb(0.97, 0.98, 1.0),
    borderColor: rgb(0.85, 0.89, 0.95),
    borderWidth: 1,
  });

  // Center the formula text
  const cleanEq = cleanTextForPdf(latex.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '($1 / $2)').replace(/\\[a-zA-Z]+/g, (m) => m.slice(1)));
  const eqText = `[ ${cleanEq} ]`;
  const textWidth = ctx.fontItalic.widthOfTextAtSize(eqText, 11);
  const eqX = (ctx.pageWidth - textWidth) / 2;

  ctx.currentPage.drawText(eqText, {
    x: eqX,
    y: boxY + 16,
    size: 11,
    font: ctx.fontItalic,
    color: rgb(0.1, 0.15, 0.25),
  });

  // Equation Number on Right
  if (block.data.equationNumber) {
    const eqNum = cleanTextForPdf(block.data.equationNumber);
    const eqNumW = ctx.fontRegular.widthOfTextAtSize(eqNum, 10);
    ctx.currentPage.drawText(eqNum, {
      x: ctx.pageWidth - ctx.marginRight - eqNumW - 12,
      y: boxY + 16,
      size: 10,
      font: ctx.fontRegular,
      color: rgb(0.4, 0.45, 0.5),
    });
  }

  ctx.currentY = boxY - 8;

  // Caption below if present
  if (block.data.caption) {
    ensureVerticalSpace(ctx, 14);
    const captionText = cleanTextForPdf(`Equation: ${block.data.caption}`);
    const capW = ctx.fontRegular.widthOfTextAtSize(captionText, 9);
    ctx.currentPage.drawText(captionText, {
      x: (ctx.pageWidth - capW) / 2,
      y: ctx.currentY,
      size: 9,
      font: ctx.fontItalic,
      color: rgb(0.45, 0.48, 0.55),
    });
    ctx.currentY -= 14;
  }
}

/**
 * Renders tabular data with cell borders and header styling
 */
function renderTableBlock(ctx: RenderContext, block: TableBlock) {
  const cells = block.data.cells || [];
  if (cells.length === 0) return;

  const numCols = cells[0].length;
  if (numCols === 0) return;

  const colWidth = ctx.contentWidth / numCols;
  const rowHeight = 22;

  for (let r = 0; r < cells.length; r++) {
    ensureVerticalSpace(ctx, rowHeight + 4);
    const row = cells[r];
    const isHeader = r === 0 && block.data.hasHeaderRow !== false;
    const rowY = ctx.currentY - rowHeight;

    // Header Background
    if (isHeader) {
      ctx.currentPage.drawRectangle({
        x: ctx.marginLeft,
        y: rowY,
        width: ctx.contentWidth,
        height: rowHeight,
        color: rgb(0.92, 0.94, 0.97),
      });
    }

    for (let c = 0; c < row.length; c++) {
      const cell = row[c];
      const cellX = ctx.marginLeft + (c * colWidth);
      const font = isHeader || cell?.bold ? ctx.fontBold : ctx.fontRegular;
      const fontSize = isHeader ? 9.5 : 9;
      const content = cleanTextForPdf((cell?.content || '').substring(0, 45));

      // Cell Border
      ctx.currentPage.drawRectangle({
        x: cellX,
        y: rowY,
        width: colWidth,
        height: rowHeight,
        borderColor: rgb(0.8, 0.84, 0.9),
        borderWidth: 0.75,
      });

      // Cell Text
      let textX = cellX + 6;
      if (cell?.alignment === 'center' || isHeader) {
        textX = cellX + (colWidth - font.widthOfTextAtSize(content, fontSize)) / 2;
      } else if (cell?.alignment === 'right') {
        textX = cellX + colWidth - font.widthOfTextAtSize(content, fontSize) - 6;
      }

      ctx.currentPage.drawText(content, {
        x: Math.max(cellX + 4, textX),
        y: rowY + 6,
        size: fontSize,
        font,
        color: isHeader ? rgb(0.1, 0.15, 0.25) : rgb(0.15, 0.18, 0.22),
      });
    }

    ctx.currentY = rowY;
  }

  ctx.currentY -= 10;

  // Caption
  if (block.data.caption) {
    ensureVerticalSpace(ctx, 14);
    const captionText = cleanTextForPdf(`Table: ${block.data.caption}`);
    ctx.currentPage.drawText(captionText, {
      x: ctx.marginLeft,
      y: ctx.currentY,
      size: 9,
      font: ctx.fontItalic,
      color: rgb(0.4, 0.45, 0.5),
    });
    ctx.currentY -= 14;
  }
}

/**
 * Embeds image data URLs into the PDF page
 */
async function renderImageBlock(ctx: RenderContext, block: ImageBlock) {
  const dataUrl = block.data.previewDataUrl;
  if (!dataUrl || !dataUrl.startsWith('data:image/')) return;

  try {
    let embeddedImg;
    const base64Data = dataUrl.split(',')[1];
    const imageBytes = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));

    if (dataUrl.includes('image/png')) {
      embeddedImg = await ctx.pdfDoc.embedPng(imageBytes);
    } else {
      embeddedImg = await ctx.pdfDoc.embedJpg(imageBytes);
    }

    const { width: origW, height: origH } = embeddedImg.scale(1);
    const targetWidth = ctx.contentWidth * ((block.data.widthPercent || 80) / 100);
    const scaleFactor = targetWidth / origW;
    const targetHeight = origH * scaleFactor;

    ensureVerticalSpace(ctx, targetHeight + 30);

    const imgY = ctx.currentY - targetHeight;
    let imgX = ctx.marginLeft;
    if (block.data.alignment === 'center') {
      imgX = ctx.marginLeft + (ctx.contentWidth - targetWidth) / 2;
    } else if (block.data.alignment === 'right') {
      imgX = ctx.pageWidth - ctx.marginRight - targetWidth;
    }

    ctx.currentPage.drawImage(embeddedImg, {
      x: imgX,
      y: imgY,
      width: targetWidth,
      height: targetHeight,
    });

    ctx.currentY = imgY - 8;

    if (block.data.caption) {
      ensureVerticalSpace(ctx, 14);
      const capText = cleanTextForPdf(`Figure: ${block.data.caption}`);
      const capW = ctx.fontItalic.widthOfTextAtSize(capText, 9);
      ctx.currentPage.drawText(capText, {
        x: (ctx.pageWidth - capW) / 2,
        y: ctx.currentY,
        size: 9,
        font: ctx.fontItalic,
        color: rgb(0.4, 0.45, 0.5),
      });
      ctx.currentY -= 14;
    }
  } catch (err) {
    console.warn('Failed to embed image block in PDF:', err);
  }
}

/**
 * Applies professional academic headers and footers with page numbering
 */
function applyHeadersAndFooters(ctx: RenderContext) {
  const totalPages = ctx.pages.length;
  const startPageIdx = (ctx.docModel.metadata.documentType === 'cover_page' || ctx.docModel.sections.some((s) => s.type === 'cover' && s.enabled)) ? 1 : 0;

  for (let i = startPageIdx; i < totalPages; i++) {
    const page = ctx.pages[i];
    const pageNum = i + 1;

    // Header
    const headerTitle = cleanTextForPdf(`${ctx.docModel.metadata.courseCode ? ctx.docModel.metadata.courseCode + ' -- ' : ''}${ctx.docModel.metadata.title || ''}`);
    if (headerTitle.trim()) {
      page.drawText(headerTitle.substring(0, 60), {
        x: ctx.marginLeft,
        y: ctx.pageHeight - ctx.marginTop + 14,
        size: 8.5,
        font: ctx.fontRegular,
        color: rgb(0.5, 0.55, 0.6),
      });

      page.drawLine({
        start: { x: ctx.marginLeft, y: ctx.pageHeight - ctx.marginTop + 8 },
        end: { x: ctx.pageWidth - ctx.marginRight, y: ctx.pageHeight - ctx.marginTop + 8 },
        thickness: 0.5,
        color: rgb(0.85, 0.88, 0.92),
      });
    }

    // Footer
    const footerText = cleanTextForPdf(`Page ${pageNum} of ${totalPages}`);
    const footerW = ctx.fontRegular.widthOfTextAtSize(footerText, 8.5);

    page.drawLine({
      start: { x: ctx.marginLeft, y: ctx.marginBottom - 8 },
      end: { x: ctx.pageWidth - ctx.marginRight, y: ctx.marginBottom - 8 },
      thickness: 0.5,
      color: rgb(0.85, 0.88, 0.92),
    });

    page.drawText(footerText, {
      x: (ctx.pageWidth - footerW) / 2,
      y: ctx.marginBottom - 20,
      size: 8.5,
      font: ctx.fontRegular,
      color: rgb(0.45, 0.48, 0.55),
    });
  }
}

/**
 * Merges multiple PDF Uint8Array byte buffers into a single combined PDF document
 */
export async function mergePdfFiles(fileBytesList: Uint8Array[]): Promise<Uint8Array> {
  const mergedPdf = await PDFDocument.create();

  for (const fileBytes of fileBytesList) {
    const srcDoc = await PDFDocument.load(fileBytes);
    const copiedPages = await mergedPdf.copyPages(srcDoc, srcDoc.getPageIndices());
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  return await mergedPdf.save();
}

/**
 * Helper to download raw bytes as a PDF file
 */
export function downloadPdfBytes(bytes: Uint8Array, filename: string = 'document.pdf') {
  const safeName = sanitizeFilename(filename, 'pdf');
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Helper to wrap text according to maximum line width
 */
function wrapText(text: string, font: PDFFont, fontSize: number, maxWidth: number): string[] {
  const cleaned = cleanTextForPdf(text);
  const words = cleaned.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    const width = font.widthOfTextAtSize(candidate, fontSize);

    if (width <= maxWidth) {
      currentLine = candidate;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  }

  if (currentLine) lines.push(currentLine);
  return lines.length > 0 ? lines : [''];
}
